/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getExams,
    updateExam,
    createExam,
    deleteExam,
    getExamResults,
    saveExamResult,
    deleteExamResult,
    getExamStatistics,
} from './examApi';

import {
    logoutUser,
} from '../user/userApi';

import { ExamState } from './types';

const initialState = {
    error: null,
    isFetching: false,
    isLoggedIn: false,
    showTwoFactorPage: false,
    exams: [],
    singleExam: null,
    examResults: [],
    examStatistics: null,
    isSaving: false,
} as ExamState;

export const examSlice = createSlice({
    name: 'exam',
    initialState,
    reducers: {
        loadSingleExam: (state, action: PayloadAction<number>) => {
            const exam = state.exams.find(e => e.id === action.payload) || null;
            state.singleExam = exam;
        },
        resetSingleExam: (state) => {
            state.singleExam = null;
        },
    },
    extraReducers: (builder) => {
        // Get exams
        builder
            .addCase(getExams.fulfilled, (state:ExamState, action) => {
                state.isFetching = false;
                state.exams = action?.payload?.exams;
            });

        // Update exam
        builder.addCase(updateExam.fulfilled, (state: ExamState, action) => {
            state.isFetching = false;
            const updatedExam = action?.payload?.exam;

            // Update singleExam
            state.singleExam = updatedExam;

            // Update exams array
            const index = state.exams.findIndex(e => e.id === updatedExam.id);
            if (index !== -1) {
                state.exams[index] = updatedExam;
            }

            notification.success({
                message: 'Success',
                description: 'Egzaminas sėkmingai atnaujintas',
            });
        });

        // Create exam
        builder.addCase(createExam.fulfilled, (state: ExamState, action) => {
            state.isFetching = false;
            const newExam = action?.payload?.exam;

            // Add to exams array
            if (newExam) {
                state.exams.push(newExam);
            }

            notification.success({
                message: 'Success',
                description: 'Egzaminas sėkmingai sukurtas',
            });
        });

        // Delete exam
        builder.addCase(deleteExam.fulfilled, (state: ExamState, action) => {
            state.isFetching = false;
            const deletedExamId = action?.payload?.id;

            // Remove from exams array
            if (deletedExamId) {
                state.exams = state.exams.filter(e => e.id !== deletedExamId);

                // Clear singleExam if it was the deleted one
                if (state.singleExam?.id === deletedExamId) {
                    state.singleExam = null;
                }
            }

            notification.success({
                message: 'Success',
                description: 'Egzaminas sėkmingai ištrintas',
            });
        });

        // Get exam results
        builder.addCase(getExamResults.fulfilled, (state: ExamState, action) => {
            state.isFetching = false;
            state.examResults = action.payload;
        });

        // Get exam statistics
        builder.addCase(getExamStatistics.fulfilled, (state: ExamState, action) => {
            state.isFetching = false;
            state.examStatistics = action.payload?.statistics || action.payload;
        });

        // Save exam result
        builder.addCase(saveExamResult.fulfilled, (state: ExamState, action) => {
            state.isSaving = false;
            state.examResults = action.payload;

            notification.success({
                message: 'Sėkmingai išsaugota',
                description: 'Egzamino rezultatai sėkmingai išsaugoti',
            });
        });

        // Delete exam result
        builder.addCase(deleteExamResult.fulfilled, (state: ExamState, action) => {
            state.isFetching = false;
            const deletedResultId = action?.payload?.id;
            if (deletedResultId) {
                state.examResults = state.examResults.filter(r => r.id !== deletedResultId);
            }
            notification.success({
                message: 'Success',
                description: 'Egzamino rezultatas sėkmingai ištrintas',
            });
        });

        // on logout
        builder
            .addCase(logoutUser.fulfilled, (state:ExamState) => {
                state.exams = [];
                state.singleExam = null;
            });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                updateExam.pending,
                createExam.pending,
                deleteExam.pending,
                deleteExamResult.pending,
            ),
            (state:ExamState) => {
                state.isFetching = true;
                state.isLoggedIn = false;
                state.error = null;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getExams.pending,
                getExamResults.pending,
                getExamStatistics.pending,
            ),
            (state:ExamState) => {
                state.isFetching = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                saveExamResult.pending,
            ),
            (state:ExamState) => {
                state.isSaving = true;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                updateExam.rejected,
                createExam.rejected,
                deleteExam.rejected,
                getExams.rejected,
                getExamResults.rejected,
                getExamStatistics.rejected,
                saveExamResult.rejected,
                deleteExamResult.rejected,
            ),
            (state:ExamState, action) => {
                state.isFetching = false;
                state.isSaving = false;
                state.error = action.error;
            },
        );
    },
});

export const { loadSingleExam, resetSingleExam } = examSlice.actions;
export default examSlice.reducer;
